import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { backend_api } from "../../../../static/server";
import { toast } from "react-toastify";

// Helper function to create paginated URL
const createPaginatedUrl = (baseUrl, page, pageSize) => {
  const params = [];

  if (page !== undefined) {
    params.push(`page=${page}`);
  }
  if (pageSize !== undefined) {
    params.push(`page_size=${pageSize}`);
  }

  if (params.length > 0) {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${params.join("&")}`;
  }

  return baseUrl;
};

const formatPaginationData = (responseData, page, pageSize) => {
  return {
    ...responseData,
    pagination: {
      page: page || 1,
      pageSize: pageSize || 10,
      rowCount: responseData.count || 0,
      nextPage: responseData.next,
      previousPage: responseData.previous,
      count: responseData.count || 0,
      next: responseData.next,
      previous: responseData.previous,
    },
  };
};

export const getPracticeArea = createAsyncThunk(
  "case/getPracticeArea",
  async ({ url, token, page, pageSize }, { rejectWithValue }) => {
    try {
      let paginatedUrl =
        url || `${backend_api}case/case-type/?case_service_type=practice_area`;
      paginatedUrl = createPaginatedUrl(paginatedUrl, page, pageSize);

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return formatPaginationData(response.data, page, pageSize);
    } catch (error) {
      toast.error("Failed to get practice areas");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getCases = createAsyncThunk(
  "case/getCases",
  async ({ url, token, page, pageSize }, { rejectWithValue }) => {
    try {
      const paginatedUrl = createPaginatedUrl(url, page, pageSize);

      const response = await axios.get(paginatedUrl, {
        headers: { Authorization: `Token ${token}` },
      });

      return formatPaginationData(response.data, page, pageSize);
    } catch (error) {
      toast.error("Failed to get cases");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getEditCaseData = createAsyncThunk(
  "getEditCaseData",
  async ({ url, token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Token ${token}` },
      });
      return response.data;
    } catch (error) {
      toast.error("Failed to get case data for editing");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const update = createAsyncThunk(
  "update",
  async ({ url, body, token }, { rejectWithValue }) => {
    try {
      const response = await fetch(url, {
        method: "PATCH", // or "PUT"
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error("Failed to update case");
      }
      return await response.json();
    } catch (error) {
      toast.error("Failed to update case");
      return rejectWithValue(error.message);
    }
  }
);

export const getCaseTemplate = createAsyncThunk(
  "case/getCaseTemplate",
  async ({ url, token, page, pageSize }, { rejectWithValue }) => {
    try {
      const paginatedUrl = createPaginatedUrl(url, page, pageSize);

      const response = await axios.get(paginatedUrl, {
        headers: { Authorization: `Token ${token}` },
      });

      return formatPaginationData(response.data, page, pageSize);
    } catch (error) {
      toast.error("Failed to get case templates");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getEdtiTempData = createAsyncThunk(
  "case/getEdtiTempData",
  async ({ url, token, page, pageSize }, { rejectWithValue }) => {
    try {
      // For single item fetch, don't use pagination parameters
      if (url.includes("/")) {
        const response = await axios.get(url, {
          headers: { Authorization: `Token ${token}` },
        });
        return response.data;
      }

      // For list fetch, use pagination
      const paginatedUrl = createPaginatedUrl(url, page, pageSize);

      const response = await axios.get(paginatedUrl, {
        headers: { Authorization: `Token ${token}` },
      });

      return formatPaginationData(response.data, page, pageSize);
    } catch (error) {
      toast.error("Failed to get template data for editing");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCase = createAsyncThunk(
  "case/createCase",
  async ({ url, body, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(url, body, {
        headers: { Authorization: `Token ${token}` },
      });
      toast.success("Case created successfully");
      return response.data;
    } catch (error) {
      toast.error("Failed to create case");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCaseTemplate = createAsyncThunk(
  "case/createCaseTemplate",
  async ({ url, body, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(url, body, {
        headers: { Authorization: `Token ${token}` },
      });
      toast.success("Case template created successfully");
      return response.data;
    } catch (error) {
      toast.error("Failed to create case template");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const CaseSlice = createSlice({
  name: "case",
  initialState: {
    allCase: null,
    allCaseTemplate: null,
    allPracticeArea: null,
    editCaseData: null,
    editcaseTemplateData: null,
    loading: false,
    error: null,
    casePagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
      count: 0,
      next: null,
      previous: null,
    },
    practiceAreaPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
      count: 0,
      next: null,
      previous: null,
    },
    caseTemplatePagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
      count: 0,
      next: null,
      previous: null,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getPracticeArea
      .addCase(getPracticeArea.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPracticeArea.fulfilled, (state, action) => {
        state.loading = false;
        state.allPracticeArea = action.payload.results;
        state.practiceAreaPagination = {
          ...action.payload.pagination,
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      })
      .addCase(getPracticeArea.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // getCases
      .addCase(getCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCases.fulfilled, (state, action) => {
        state.loading = false;
        state.allCase = action.payload.results;
        state.casePagination = {
          ...action.payload.pagination,
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      })
      .addCase(getCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // getCaseTemplate
      .addCase(getCaseTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCaseTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.allCaseTemplate = action.payload.results;
        state.caseTemplatePagination = {
          ...action.payload.pagination,
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      })
      .addCase(getCaseTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getEdtiTempData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEdtiTempData.fulfilled, (state, action) => {
        state.loading = false;
        state.editcaseTemplateData = action.payload;
      })
      .addCase(getEdtiTempData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // createCase
      .addCase(createCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCase.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // createCaseTemplate
      .addCase(createCaseTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCaseTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCaseTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //edit case data
      .addCase(getEditCaseData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEditCaseData.fulfilled, (state, action) => {
        state.loading = false;
        state.editCaseData = action.payload;
      })
      .addCase(getEditCaseData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(update.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(update.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(update.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const CaseReducer = CaseSlice.reducer;
